import { Injectable } from '@angular/core';
import Intercom, { boot, shutdown, show, update } from '@intercom/messenger-js-sdk';
import * as CryptoJS from 'crypto-js';
import { environment } from '@environments/environment';
import { User } from '@app/models';
import { AuthService } from './auth.service';
import { SettingsService } from './settings.service';
import { distinctUntilChanged } from 'rxjs/operators';

type IntercomSettings = Parameters<typeof boot>[0];

@Injectable({
  providedIn: 'root',
})
export class IntercomService {
  private initialized = false;
  private bootPromise: Promise<void> | null = null;
  private lastBootKey: string | null = null;

  constructor(
    private authService: AuthService,
    private settingsService: SettingsService,
  ) {
    this.authService.isAuthenticated.pipe(
      distinctUntilChanged(),
    ).subscribe((isAuthenticated) => {
      if (isAuthenticated) {
        void this.ensureBoot(this.buildIdentifiedSettings());
      } else {
        void this.ensureBoot(Promise.resolve(this.buildGuestSettings()));
      }
    });
  }

  openMessenger() {
    void this.ensureBoot(
      this.authService.isAuthenticated.value
        ? this.buildIdentifiedSettings()
        : Promise.resolve(this.buildGuestSettings())
    ).then(() => show());
  }

  private buildGuestSettings(): IntercomSettings {
    return {
      app_id: environment.intercomAppId,
      hide_default_launcher: true,
    };
  }

  private async buildIdentifiedSettings(): Promise<IntercomSettings | null> {
    if (!this.authService.isAuthenticated.value) {
      return null;
    }

    const authUser = this.authService.userValue;
    let currentUser: User | null = authUser;
    let currentClient: { uuid?: string; name?: string } | null = null;
    let userRoles: string[] = authUser?.roles_name || [];

    try {
      const settings = await this.settingsService.getSettings();
      if (settings?.current_user) {
        currentUser = { ...authUser, ...settings.current_user };
      }
      currentClient = settings?.client_features ?? null;
      userRoles = settings?.user_roles || userRoles;
    } catch (error) {
      console.warn('Intercom: settings unavailable, using auth user', error);
    }

    const userIdentifier = currentUser?.uuid;
    if (!userIdentifier) {
      console.warn('Intercom: user uuid not available yet');
      return null;
    }

    const hash = CryptoJS.HmacSHA256(userIdentifier, environment.intercomSecretKey).toString(CryptoJS.enc.Hex);
    const adminRoles = ['admin', 'client_admin', 'sifa_admin', 'sifa_master_admin'];

    return {
      app_id: environment.intercomAppId,
      user_id: userIdentifier,
      user_hash: hash,
      email: currentUser.email,
      mcc_username: currentUser.username,
      name: [currentUser.firstname, currentUser.name].filter(Boolean).join(' '),
      salutation: currentUser.salutation,
      mcc_roles: userRoles.join(','),
      mcc_admin: userRoles.some((role) => adminRoles.includes(role)),
      hide_default_launcher: true,
      company: currentClient?.uuid ? {
        company_id: currentClient.uuid,
        name: currentClient.name,
      } : undefined,
      Companies: currentClient?.uuid ? [{
        id: currentClient.uuid,
        name: currentClient.name,
      }] : undefined,
    };
  }

  private async ensureBoot(settingsPromise: Promise<IntercomSettings | null>): Promise<void> {
    const settings = await settingsPromise;
    if (!settings) {
      return;
    }

    const bootKey = JSON.stringify({
      user_id: settings.user_id ?? null,
      user_hash: settings.user_hash ?? null,
      app_id: settings.app_id,
    });

    if (this.bootPromise && this.lastBootKey === bootKey) {
      return this.bootPromise;
    }

    this.lastBootKey = bootKey;
    this.bootPromise = this.applyBoot(settings);
    return this.bootPromise;
  }

  private applyBoot(settings: IntercomSettings): Promise<void> {
    return new Promise((resolve) => {
      if (typeof window !== 'undefined') {
        window.intercomSettings = settings;
      }

      if (this.initialized) {
        shutdown();
        boot(settings);
        if (settings.user_id && settings.user_hash) {
          update(settings);
        }
      } else {
        Intercom(settings);
        this.initialized = true;
      }

      // Allow the widget loader to process boot before show().
      window.setTimeout(() => resolve(), 150);
    });
  }
}
